import React from "react";

// ---------------------------------------------------------------------------
// MarkdownText — tiny zero-dependency markdown renderer scoped to what an
// LLM actually emits in a chat reply. Supports:
//   - **bold**
//   - *italic*  (only when not adjacent to another *, so we don't fight bold)
//   - `inline code`
//   - [label](url)  → opens in a new tab
//   - Line breaks (\n)
//   - "- " / "* " bullet lines  → real list items
//   - "1. " ordered list lines
//   - Headings #, ##, ### at line-start
//
// Pure React nodes — no dangerouslySetInnerHTML — so XSS via injected HTML
// is impossible. Inline styles only so it matches the rest of the panel.
// ---------------------------------------------------------------------------

const INLINE_REGEX =
  /\*\*([^*]+?)\*\*|`([^`]+?)`|\[([^\]]+?)\]\(([^)\s]+?)\)|\*([^*\s][^*]*?[^*\s]|[^*\s])\*/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = INLINE_REGEX.exec(text)) !== null) {
    if (m.index > lastIndex) {
      out.push(text.slice(lastIndex, m.index));
    }
    const k = `${keyPrefix}-${key++}`;
    if (m[1] !== undefined) {
      out.push(<strong key={k}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      out.push(
        <code
          key={k}
          style={{
            background: "rgba(0,0,0,0.06)",
            padding: "0 4px",
            borderRadius: 4,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.92em",
          }}
        >
          {m[2]}
        </code>
      );
    } else if (m[3] !== undefined && m[4] !== undefined) {
      out.push(
        <a
          key={k}
          href={m[4]}
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: "var(--color-success, #156548)", textDecoration: "underline" }}
        >
          {m[3]}
        </a>
      );
    } else if (m[5] !== undefined) {
      out.push(<em key={k}>{m[5]}</em>);
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out;
}

interface Block {
  type: "p" | "ul" | "ol" | "h1" | "h2" | "h3";
  items?: string[];   // for ul/ol
  text?: string;      // for p / heading
}

function parseBlocks(content: string): Block[] {
  const lines = content.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  const flushParagraph = (buf: string[]) => {
    if (!buf.length) return;
    const joined = buf.join("\n");
    if (joined.trim()) blocks.push({ type: "p", text: joined });
    buf.length = 0;
  };

  let pBuf: string[] = [];

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Heading
    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) {
      flushParagraph(pBuf);
      const level = h[1].length as 1 | 2 | 3;
      blocks.push({ type: (`h${level}`) as Block["type"], text: h[2] });
      i++;
      continue;
    }

    // Unordered list (consecutive `- ` or `* ` lines)
    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph(pBuf);
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list (consecutive `1. `-style lines)
    if (/^\d+\.\s+/.test(trimmed)) {
      flushParagraph(pBuf);
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Blank line → paragraph break
    if (trimmed === "") {
      flushParagraph(pBuf);
      i++;
      continue;
    }

    // Plain paragraph line
    pBuf.push(line);
    i++;
  }
  flushParagraph(pBuf);
  return blocks;
}

export default function MarkdownText({ content }: { content: string }) {
  const blocks = parseBlocks(content);
  return (
    <>
      {blocks.map((b, idx) => {
        const key = `b${idx}`;
        if (b.type === "p") {
          // Render line breaks inside a paragraph as <br/>.
          const lines = (b.text ?? "").split("\n");
          return (
            <div key={key} style={{ marginBottom: idx === blocks.length - 1 ? 0 : 6 }}>
              {lines.map((ln, j) => (
                <React.Fragment key={`${key}-l${j}`}>
                  {renderInline(ln, `${key}-l${j}`)}
                  {j < lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          );
        }
        if (b.type === "ul" || b.type === "ol") {
          const Tag = b.type === "ul" ? "ul" : "ol";
          return (
            <Tag
              key={key}
              style={{
                margin: "4px 0",
                paddingLeft: 20,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {(b.items ?? []).map((it, j) => (
                <li key={`${key}-i${j}`}>{renderInline(it, `${key}-i${j}`)}</li>
              ))}
            </Tag>
          );
        }
        // headings
        const sizes: Record<string, number> = { h1: 16, h2: 14.5, h3: 13.5 };
        return (
          <div
            key={key}
            style={{
              fontWeight: 700,
              fontSize: sizes[b.type],
              margin: "4px 0 2px 0",
            }}
          >
            {renderInline(b.text ?? "", key)}
          </div>
        );
      })}
    </>
  );
}
